import AuthClient from "../api/AuthClient";
import GetSASTokenApi from "../api/GetSASTokenApi";
import { AzureBlob } from "../types";
import { uploadImageToStorage } from "./fileUpload";

interface SasTokenOptions {
    authClient: AuthClient;
    isFriend: boolean;
    userId: string;
    profileCount: number;
}

interface ProfileUploadOptions {
    authClient: AuthClient;
    file: File | null;
    profileId: string;
    profileCount: number;
    setAsProfilePhoto: boolean;
}

const normalizeAzureBlobs = (data: AzureBlob[] | AzureBlob | undefined): AzureBlob[] => {
    if (!data) {
        return [];
    }

    return Array.isArray(data) ? data : [data];
};

export const getBlobUploadTargets = async ({
    authClient,
    isFriend,
    userId,
    profileCount,
}: SasTokenOptions): Promise<AzureBlob[]> => {
    const getSasTokenApi = new GetSASTokenApi(authClient);
    const sasToken = await getSasTokenApi.getSasToken(isFriend, userId, profileCount);

    return normalizeAzureBlobs(sasToken.data);
};

const getRequiredBlobsByIndexes = (azureBlobs: AzureBlob[], indexes: number[]) => {
    const targetBlobs = indexes
        .map((index) => azureBlobs[index])
        .filter((blob): blob is AzureBlob => Boolean(blob));

    if (targetBlobs.length !== indexes.length) {
        throw new Error(`Expected SAS targets at indexes ${indexes.join(", ")}.`);
    }

    return targetBlobs;
};

export const uploadImageWithThumbnail = async (
    file: File | null,
    azureBlobs: AzureBlob[],
) => {
    if (!file) {
        return;
    }

    await uploadImageToStorage(file, azureBlobs);
};

export const uploadUserProfileImage = async ({
    authClient,
    file,
    profileId,
    profileCount,
    setAsProfilePhoto,
}: ProfileUploadOptions) => {
    if (!file) {
        return profileCount;
    }

    const numericProfileCount = Number(profileCount ?? 0);

    const azureBlobs = await getBlobUploadTargets({
        authClient,
        isFriend: false,
        userId: profileId,
        profileCount: numericProfileCount,
    });

    const uploadTargets = setAsProfilePhoto
        ? getRequiredBlobsByIndexes(azureBlobs, [0, 1])
        : getRequiredBlobsByIndexes(azureBlobs, [2, 3]);

    await uploadImageWithThumbnail(file, uploadTargets);

    return setAsProfilePhoto ? numericProfileCount : numericProfileCount + 1;
};
